/**
 * Typed Event Bus — decoupled cross-panel communication
 */
import type { SimEventMap } from '@graphicsim/shared-types';

type EventHandler<T> = (payload: T) => void;

class EventBus {
  private handlers = new Map<string, Set<EventHandler<any>>>();

  on<K extends keyof SimEventMap>(event: K, handler: EventHandler<SimEventMap[K]>): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);
    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  off<K extends keyof SimEventMap>(event: K, handler: EventHandler<SimEventMap[K]>): void {
    this.handlers.get(event)?.delete(handler);
  }

  emit<K extends keyof SimEventMap>(event: K, payload: SimEventMap[K]): void {
    const set = this.handlers.get(event);
    if (set) {
      set.forEach((handler) => {
        try {
          handler(payload);
        } catch (err) {
          console.error(`EventBus error in handler for "${event}":`, err);
        }
      });
    }
  }

  /** Remove all handlers for a specific event, or all events if none specified */
  clear(event?: keyof SimEventMap): void {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers.clear();
    }
  }
}

export const eventBus = new EventBus();
